import { CookiesKey } from '@starsoft/common/enums';
import {
  dataParser,
  interceptorDataParser,
} from '@starsoft/common/utils/parsers';
import { QueryClient } from '@tanstack/react-query';
import axios, { AxiosInstance } from 'axios';
import { Buffer } from 'buffer';
import { NextRouter } from 'next/router';
import { Navigate as ReactRouter } from 'react-router-dom';
import { AuthService } from '../AuthService';
import { Either } from '../MoneyFormatter';
import moment from 'moment-timezone';
import { getCookie, hasCookie } from 'cookies-next';

const customParamsSerializer = params => {
  const serializedParams = {};

  const isBrazilOrigin = true;

  const serializeTimeZone = params?.serializeTimeZone;

  for (const key in params) {
    if (key === 'serializeTimeZone') {
      continue;
    }

    if (params.hasOwnProperty(key)) {
      const value = params[key];

      if (value === null || value === undefined) {
        continue;
      }

      // Set default value first
      serializedParams[key] = value;

      // Handle date formatting based on origin
      if (key === 'startDate' && value instanceof Date) {
        let startMoment = moment(value).startOf('day').locale('pt-br');

        if (serializeTimeZone === true) {
          startMoment = startMoment.subtract(6, 'hours');
        }

        serializedParams[key] = startMoment.toISOString();
      }

      if (key === 'endDate' && value instanceof Date) {
        let endMoment = moment(value).endOf('day').locale('pt-br');
        if (serializeTimeZone === true) {
          endMoment = endMoment.subtract(6, 'hours');
        }

        serializedParams[key] = endMoment.toISOString();
      }
    }
  }
  return new URLSearchParams(serializedParams).toString();
};

function getTokenExpirationSeconds(token: string): number {
  try {
    const payloadBase64 = token?.split?.('.')?.[1];
    const decodedPayload = JSON.parse(
      Buffer.from(payloadBase64, 'base64').toString(),
    );

    const expirationTimestamp = decodedPayload.exp;
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const secondsRemaining = expirationTimestamp - nowInSeconds;

    return secondsRemaining;
  } catch (error) {
    AuthService.setAsUnauthorized();
  }
}

export class APIClient {
  public instance: AxiosInstance;
  private authTokenCookie: string;
  private refreshTokenCookie: string;
  private isAdmin: boolean;
  private queryClient: QueryClient;
  private isRefreshingToken: boolean = false;
  constructor(
    baseURL: string,
    tenantId: string,
    navigation: Either<NextRouter, typeof ReactRouter>,
    queryClient: QueryClient,
    isAdmin: boolean,
  ) {
    this.instance = axios.create({
      baseURL: baseURL + '/v1/',
      withCredentials: true,
      timeout: isAdmin ? 40000 : 10000,
      paramsSerializer: customParamsSerializer,
      headers: {
        'x-lang': 'pt',
        'x-tenant-id': tenantId,
      },
    });
    this.isAdmin = isAdmin;
    this.authTokenCookie = isAdmin ? CookiesKey.TokenAdmin : CookiesKey.Token;
    this.refreshTokenCookie = isAdmin
      ? CookiesKey.RefreshTokenAdmin
      : CookiesKey.RefreshToken;
    this.queryClient = queryClient;
    this.setupInterceptors();
  }

  private async axiosInterceptorRefreshToken(): Promise<boolean> {
    let retries = 0;
    const maxRetries = 3;

    while (retries < maxRetries) {
      try {
        if (this.isRefreshingToken) {
          return true;
        }
        this.isRefreshingToken = true;
        const response = await this.instance.post('iam/refresh');
        AuthService.setToken(response.data.accessToken, this.authTokenCookie);
        AuthService.setRefreshToken(
          response.data.refreshToken,
          this.refreshTokenCookie,
        );
        this.instance.defaults.headers['authorization'] =
          response.data.accessToken;
        this.instance.defaults.headers['x-refresh-token'] =
          response.data.refreshToken;
        this.isRefreshingToken = false;
        return true;
      } catch (error) {
        this.isRefreshingToken = false;
        retries++;
        if (retries === maxRetries) {
          AuthService.logOut();
          return false;
        }
      }
    }
    return false;
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(async request => {
      if (!request.headers) {
        return request;
      }

      request.headers['x-lang'] = 'pt';

      if (process.env.NODE_ENV === 'development') {
        request.headers['ip'] = '201.55.122.13';
        request.headers['x-ip'] = '201.55.122.13';
        request.headers['x-forwarded-for'] = '201.55.122.13';
        request.headers['x-real-ip'] = '201.55.122.13';
        request.headers['x-client-ip'] = '201.55.122.13';
        // request.headers['user-agent'] =
        //   'Mozilla/5.0 (iPhone; CPU iPhone OS 18_7 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/26.0.1 Mobile/15E148 Safari/604.1';
      }

      if (request.headers['x-challenge-id']) {
        this.instance.defaults.headers['x-challenge-id'] =
          request.headers['x-challenge-id'];
      }

      if (
        hasCookie(this.authTokenCookie) &&
        !request.headers['authorization']
      ) {
        request.headers['authorization'] = getCookie(this.authTokenCookie);
        this.instance.defaults.headers['authorization'] = getCookie(
          this.authTokenCookie,
        ) as string;
      }

      if (
        hasCookie(this.refreshTokenCookie) &&
        !request.headers['x-refresh-token']
      ) {
        request.headers['x-refresh-token'] = getCookie(this.refreshTokenCookie);
        this.instance.defaults.headers['x-refresh-token'] = getCookie(
          this.refreshTokenCookie,
        ) as string;
      }

      // if (!!this.tenantId) {
      //   request.headers['x-tenant-id'] = this.tenantId;
      // }

      return {
        ...request,
        paramsSerializer: customParamsSerializer,
        data: dataParser(request.data),
      };
    });

    this.instance.interceptors.response.use(
      response => {
        if (response?.data?.accessToken) {
          AuthService.setToken(response.data.accessToken, this.authTokenCookie);
          this.instance.defaults.headers['authorization'] =
            response.data.accessToken;
        }

        if (response?.data?.refreshToken) {
          AuthService.setRefreshToken(
            response.data.refreshToken,
            this.refreshTokenCookie,
          );
          this.instance.defaults.headers['x-refresh-token'] =
            response.data.refreshToken;
          response.headers['x-refresh-token'] = response.data.refreshToken;
        }

        return {
          ...response,
          data: interceptorDataParser(response.data),
        };
      },
      async error => {
        const originalRequest = error?.config ?? {};
        const status =
          error?.response?.status ?? error?.response?.data?.statusCode;

        if (originalRequest?.url?.includes('/players/me')) {
          return Promise.reject(error);
        }

        if (originalRequest?.url?.includes('/iam/login')) {
          return Promise.reject(error);
        }

        // Avoid retry loop for the refresh endpoint itself
        if (originalRequest?.url?.includes('iam/refresh')) {
          return Promise.reject(error);
        }

        // Handle 401: try refresh then retry the original request once
        if (status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshToken =
            this.instance.defaults.headers['x-refresh-token'] ??
            error?.response?.headers?.['x-refresh-token'];

          if (refreshToken) {
            const tokenExpiration = getTokenExpirationSeconds(refreshToken);
            if (
              tokenExpiration &&
              tokenExpiration < 50 &&
              tokenExpiration > 0
            ) {
              const refreshed = await this.axiosInterceptorRefreshToken();
              if (refreshed) {
                originalRequest.headers = {
                  ...(originalRequest.headers || {}),
                  authorization:
                    this.instance.defaults.headers['authorization'],
                  'x-refresh-token':
                    this.instance.defaults.headers['x-refresh-token'],
                };
                return this.instance.request(originalRequest);
              }
            }
          }

          AuthService.logOut(undefined, this.isAdmin, this.queryClient);
          this.queryClient.cancelQueries();
          if (this.isAdmin === false && typeof window !== 'undefined') {
            window.open('/', '_self');
          }
          return Promise.reject(error);
        }

        // Handle 403: do not redirect to avoid infinite loops (e.g., Cloudflare blocks)
        if (status === 403) {
          return Promise.reject(error);
        }

        return Promise.reject(error);
      },
    );
  }
}
